import { Component, OnInit, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';

import { PageNames } from 'src/app/configs/page-names.enum';
import { KibanaDashboardsService } from 'src/app/core/api/services';
import { EventTypes } from 'src/app/core/constants/event-types';
import { ConsoleService } from 'src/app/core/services/console.service';
import {
  CustomResultOfString,
  GetKibanaDashboardByIdDto,
  GetKibanaDashboardsWithPaginationDto,
  PaginatedListOfGetKibanaDashboardsWithPaginationDto,
} from '../../../../app/core/api/models';
import { AppDomService } from '../../../core/services/dom.service';
import { BaseIndexPageComponent } from '../../shared/base/components/base-index-page.component';
import { BaseIndexModule } from '../../shared/base/modules/base-index.module';
import { FormPageConfig } from './../../../core/models/form-page-config.model';
import { KibanaDashboardsFormComponent } from './kibana-dashboards-form/kibana-dashboards-form.component';

@Component({
  selector: 'app-kibana-dashboards',
  templateUrl: './kibana-dashboards.component.html',
  imports: [BaseIndexModule],
})
export class KibanaDashboardsComponent
  extends BaseIndexPageComponent
  implements OnInit
{
  //#region Properties

  pageName = PageNames.kibanaDashboards;

  // Binding service endpoints
  protected currentService = inject(KibanaDashboardsService);
  paginationApiMethodName: string =
    this.currentService.kibanaDashboardsGetKibanaDashboardsWithPagination.name;
  deleteApiMethodName: string = this.currentService.kibanaDashboardsDelete.name;

  modelWithPagination$: Observable<PaginatedListOfGetKibanaDashboardsWithPaginationDto>;
  items$: Observable<Array<GetKibanaDashboardsWithPaginationDto>>;
  formPageConfig: FormPageConfig<GetKibanaDashboardByIdDto> = {};

  //#endregion

  constructor() {
    super();
  }

  ngOnInit() {
    try {
      super.ngOnInit();
      this.pageConfig.showAuditBtn = false;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  configTableColumns() {
    this.tableColumns = [
      { title: this.localizer.title, name: 'title', hasSearch: true },
      {
        title: this.localizer.createDate,
        name: 'created',
        hasSearch: true,
        type: 'dateTime',
      },
    ];
  }

  onCreate(type?: any, isFormModal?: boolean) {
    try {
      if (isFormModal) {
        const timeout = setTimeout(() => {
          this.pageConfig.isVisibleForm = false;
          clearTimeout(timeout);
        }, 10);
        this.openCreateFormModal();
      }
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  //#region Api

  //Get ById
  baseGetByIdApi(id: number): void {
    this.unSubs();

    this.pageConfig.loading = true;

    this.formPageConfig.editId = id;

    this.currentService
      .kibanaDashboardsUpdateGet({ Id: id })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseGetByIdObserverFormModal);
  }

  //Delete
  baseDeleteApi(id: number) {
    this.unSubs();

    this.pageConfig.loading = true;
    this.lastSelectedItemId = id;

    this.currentService
      .kibanaDashboardsDelete({ body: { id: id } })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseDeleteObserver);
  }

  //#endregion

  //#region call Actions

  openCreateFormModal() {
    try {
      this.unSubs();

      let data = {
        title: `${this.formPageConfig?.vocabs}addTitle`,
        isEdit: false,
      };

      const modalResult = this.dialogService.openModal({
        component: KibanaDashboardsFormComponent,
        data: data,
      });

      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => (this.pageConfig.isVisibleForm = undefined)),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((res) => {
          if (res) {
            this.getTableData();
            this.cd.detectChanges();
          }
        });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  openEditFormModal(model: GetKibanaDashboardByIdDto) {
    try {
      this.unSubs();

      let data = {
        title: `${this.formPageConfig?.vocabs}editTitle`,
        model: { title: model.title, htmlContent: model.htmlContent },
        id: model.id,
        isEdit: true,
      };

      const modalResult = this.dialogService.openModal({
        component: KibanaDashboardsFormComponent,
        data: data,
      });

      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((res) => {
          if (res) {
            this.getTableData();
            this.cd.detectChanges();
          }
        });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  onShowDashboard(id) {
    this.unSubs();

    this.pageConfig.loading = true;

    this.currentService
      .kibanaDashboardsGetKibanaDashboardHtmlContentById({ Id: id })
      .pipe(this.basePipeOperators())
      .subscribe(
        (result: CustomResultOfString) => {
          if (result.isSucceed) {
            window.open(result?.data, '_blank');
          }
        },
        (error) => {
          ConsoleService.error('error', error);
        },
        () => (this.pageConfig.loading = false)
      );
  }
  //#endregion
}
